import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

import { ApiError, apiGet, apiSend, ensureCsrf, readCsrfCookie } from "./client";
import type {
  AssistantAction,
  AssistantConversation,
  Cluster,
  Credits,
  StoreChange,
  AssistantMessage,
  Finding,
  Profile,
  Run,
  User,
} from "./types";

/** Current user, or null when the session is missing/expired. */
export function useMe() {
  return useQuery<User | null>({
    queryKey: ["me"],
    queryFn: async () => {
      try {
        return await apiGet<User>("/api/auth/me");
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
    staleTime: 5 * 60_000,
  });
}

/** Anmeldung per Code: erst anfordern … */
export function useRequestCode() {
  return useMutation({
    mutationFn: (payload: { email: string }) =>
      apiSend<{ detail: string }>("POST", "/api/auth/code/request", payload),
  });
}

/** … dann einlösen. Die Sitzung hält danach dreißig Tage. */
export function useVerifyCode() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { email: string; code: string }) =>
      apiSend<User>("POST", "/api/auth/code/verify", payload),
    onSuccess: (user) => {
      // A different account may sign in after session expiry on this browser.
      queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== "me" });
      queryClient.setQueryData(["me"], user);
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiSend<{ detail: string }>("POST", "/api/auth/logout"),
    onSuccess: () => {
      queryClient.setQueryData(["me"], null);
      queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== "me" });
    },
  });
}

export function useProfiles(enabled: boolean) {
  return useQuery<Profile[]>({
    queryKey: ["profiles"],
    queryFn: () => apiGet<Profile[]>("/api/profiles"),
    enabled,
  });
}

export function usePatchProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: { id: string } & Record<string, unknown>) =>
      apiSend<Profile>("PATCH", `/api/profiles/${id}`, patch),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["profiles"] }),
  });
}

const ACTIVE_RUN_STATES = ["queued", "running"];

export function useRuns(profileSlug: string | undefined) {
  return useQuery<Run[]>({
    queryKey: ["runs", profileSlug],
    queryFn: () => apiGet<Run[]>(`/api/runs?profile=${encodeURIComponent(profileSlug ?? "")}`),
    enabled: Boolean(profileSlug),
    // Discover server-started jobs too, including jobs completed between polls.
    refetchInterval: (query) =>
      (query.state.data ?? []).some((run) => ACTIVE_RUN_STATES.includes(run.status))
        ? 5000
        : 15000,
  });
}

export function useActiveRun(profileSlug: string | undefined) {
  const runs = useRuns(profileSlug);
  return (runs.data ?? []).find((run) => ACTIVE_RUN_STATES.includes(run.status)) ?? null;
}

export function useStartRun(profileSlug: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { profile_id: string; query?: string }) =>
      apiSend<Run>("POST", "/api/runs", payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["runs", profileSlug] });
      void queryClient.invalidateQueries({ queryKey: ["findings"] });
    },
  });
}

export function useStopRun(profileSlug: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (runId: string) => apiSend<Run>("POST", `/api/runs/${runId}/stop`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["runs", profileSlug] });
    },
  });
}

/** Refreshes the findings list once a run has finished — the inventory it
 *  produced is exactly what the user is looking at. */
export function useRefreshFindingsAfterRun(profileSlug: string | undefined) {
  const runs = useRuns(profileSlug);
  const activeRun = (runs.data ?? []).find((run) => ACTIVE_RUN_STATES.includes(run.status)) ?? null;
  const queryClient = useQueryClient();
  const previous = useRef<{ profile: string | undefined; marker: string } | null>(null);
  const marker = runs.data?.map((run) => `${run.id}:${run.status}:${run.finished_at}`).join("|");

  useEffect(() => {
    if (marker === undefined) return;
    if (previous.current && previous.current.profile === profileSlug && previous.current.marker !== marker) {
      void queryClient.invalidateQueries({ queryKey: ["findings"] });
    }
    previous.current = { profile: profileSlug, marker };
  }, [marker, profileSlug, queryClient]);

  return activeRun;
}

/** What this agent has done, newest first. */
export function useHistory(profileSlug: string | undefined) {
  return useQuery<StoreChange[]>({
    queryKey: ["history", profileSlug],
    queryFn: () =>
      apiGet<StoreChange[]>(`/api/history?profile=${encodeURIComponent(profileSlug ?? "")}`),
    enabled: Boolean(profileSlug),
  });
}

/** Undo an operation and everything after it. */
export function useRevertChange(profileSlug: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (changeId: string) =>
      apiSend<{ detail: string }>("POST", `/api/history/${changeId}/revert`, {}),
    onSuccess: () => {
      // The inventory is a different one now — everything that shows it has
      // to ask again.
      void queryClient.invalidateQueries({ queryKey: ["history", profileSlug] });
      void queryClient.invalidateQueries({ queryKey: ["findings"] });
      void queryClient.invalidateQueries({ queryKey: ["clusters"] });
    },
  });
}

/** What is left of the month. One credit is one euro, and every model call
 *  and every web search counts against it. */
export function useCredits() {
  return useQuery<Credits>({
    queryKey: ["credits"],
    queryFn: () => apiGet<Credits>("/api/credits"),
    // Cheap, and it moves while the assistant works.
    refetchInterval: 20000,
  });
}

export function useClusters(type: "funding" | "technology", enabled: boolean) {
  return useQuery<Cluster[]>({
    queryKey: ["clusters", type],
    queryFn: () => apiGet<Cluster[]>(`/api/findings/clusters?type=${type}`),
    enabled,
  });
}

export function useFindings(type: "funding" | "technology", enabled: boolean) {
  return useQuery<Finding[]>({
    queryKey: ["findings", type],
    queryFn: () => apiGet<Finding[]>(`/api/findings?type=${type}`),
    enabled,
  });
}

/** Opening an entry clears its "NEU" badge — for this reader only. */
export function useMarkSeen(type: "funding" | "technology") {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (findingId: string) =>
      apiSend<{ detail: string }>("POST", "/api/findings/seen", { id: findingId }),
    onMutate: (findingId: string) => {
      // The badge goes the moment it is clicked; the request only records it.
      queryClient.setQueryData<Finding[]>(["findings", type], (items) =>
        (items ?? []).map((item) =>
          item.id === findingId ? { ...item, is_new: false } : item,
        ),
      );
    },
  });
}

/** Set the relevance by hand. A run leaves a human verdict standing. */
export function useSaveScore() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ findingId, score }: { findingId: string; score: number }) =>
      apiSend<{ detail: string }>("POST", "/api/findings/score", {
        id: findingId,
        score,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["findings"] });
    },
  });
}

/** A note of one's own on an entry — stored in the note itself. */
export function useSaveNote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ findingId, note }: { findingId: string; note: string }) =>
      apiSend<{ detail: string }>("POST", "/api/findings/note", { id: findingId, note }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["findings"] });
    },
  });
}

/** Dissolve a theme; its entries stay and lose their theme. */
export function useRemoveCluster() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ slug }: { slug: string }) =>
      apiSend<{ detail: string }>("POST", "/api/findings/clusters/remove", { slug }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["findings"] });
      void queryClient.invalidateQueries({ queryKey: ["clusters"] });
    },
  });
}

/** Archived, not deleted: the note keeps its evidence and can be restored. */
export function useRemoveFinding() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ findingId, reason }: { findingId: string; reason?: string }) =>
      apiSend<{ detail: string }>("POST", "/api/findings/remove", {
        id: findingId,
        reason: reason ?? "",
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["findings"] });
      void queryClient.invalidateQueries({ queryKey: ["clusters"] });
    },
  });
}

export function useFeedback() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ findingId, isRelevant }: { findingId: string; isRelevant: boolean }) =>
      // The id is the note path in the knowledge bundle, so it goes in the
      // body rather than the URL.
      apiSend<{ detail: string }>("POST", "/api/findings/feedback", {
        id: findingId,
        is_relevant: isRelevant,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["findings"] });
    },
  });
}

export function useAssistantConversations(profileId: string | undefined) {
  return useQuery<AssistantConversation[]>({
    queryKey: ["assistant-conversations", profileId],
    queryFn: () =>
      apiGet<AssistantConversation[]>(
        `/api/assistant/conversations?profile_id=${encodeURIComponent(profileId ?? "")}`,
      ),
    enabled: Boolean(profileId),
    refetchInterval: 5000,
  });
}

/** Messages of one thread. `null` is a thread that does not exist yet —
 *  nothing to fetch, and nothing to show but the greeting. */
export function useAssistantHistory(
  profileId: string | undefined,
  conversationId: string | null,
  polling = true,
) {
  return useQuery<AssistantMessage[]>({
    queryKey: ["assistant", profileId, conversationId],
    queryFn: () =>
      apiGet<AssistantMessage[]>(
        `/api/assistant/history?profile_id=${encodeURIComponent(profileId ?? "")}` +
          `&conversation_id=${encodeURIComponent(conversationId ?? "")}`,
      ),
    enabled: Boolean(profileId) && Boolean(conversationId),
    refetchInterval: polling ? 5000 : false,
  });
}

export interface StreamEvent {
  type: "start" | "status" | "thinking" | "text" | "tool_start" | "tool_end" | "error" | "done" | "snapshot";
  sequence?: number;
  snapshot?: AssistantProgress;
  phase?: "model" | "thinking" | "tool_input" | "answer";
  text?: string;
  tool?: string;
  input?: Record<string, unknown>;
  action?: AssistantAction;
  detail?: string;
  user_message?: AssistantMessage;
  assistant_message?: AssistantMessage;
  conversation_id?: string;
  conversation?: AssistantConversation;
}

export interface AssistantProgress {
  sequence: number;
  start?: StreamEvent | null;
  status?: StreamEvent | null;
  text?: string;
  thinking?: string;
  actions?: AssistantAction[];
  running_tool?: string | null;
  error?: StreamEvent | null;
  done?: StreamEvent | null;
}

const PROGRESS_INTERVAL_MS = 3000;

/** SSE stays the fast path. If a proxy holds the response, short requests
 *  read snapshots of the SAME turn. Sequence numbers prevent a late stream
 *  from replaying text or tool steps already shown by a snapshot. */
export async function streamAssistant(
  profileId: string,
  question: string,
  conversationId: string | null,
  onEvent: (event: StreamEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  await ensureCsrf();
  const requestId = crypto.randomUUID();
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal?.addEventListener("abort", abort, { once: true });
  if (signal?.aborted) abort();
  let sequence = 0;
  let started = false;
  let completed = false;
  let stopped = false;
  let lastStreamEvent = Date.now();
  let timer: ReturnType<typeof setTimeout> | undefined;
  let resolveDone: () => void = () => {};
  const done = new Promise<void>((resolve) => { resolveDone = resolve; });

  const deliver = (event: StreamEvent) => {
    if (stopped || completed || controller.signal.aborted) return;
    if (event.type === "start") {
      if (started) return;
      started = true;
    }
    onEvent(event);
    if (event.type === "done") {
      completed = true;
      resolveDone();
    }
  };

  const poll = async () => {
    try {
      if (Date.now() - lastStreamEvent >= PROGRESS_INTERVAL_MS) {
        const query = new URLSearchParams({ profile_id: profileId, request_id: requestId });
        const response = await fetch(`/api/assistant/progress?${query}`, {
          credentials: "same-origin", cache: "no-store", signal: controller.signal,
        });
        if (response.ok) {
          const snapshot = await response.json() as AssistantProgress;
          if (!stopped && !completed && !controller.signal.aborted && snapshot.sequence > sequence) {
            sequence = snapshot.sequence;
            if (snapshot.start) deliver(snapshot.start);
            deliver({ type: "snapshot", snapshot });
            if (snapshot.error) deliver(snapshot.error);
            if (snapshot.done) deliver(snapshot.done);
          }
        }
      }
    } catch {
      // A failed fallback read does not discard the still-running stream.
    } finally {
      if (!stopped && !completed && !controller.signal.aborted) timer = setTimeout(() => { void poll(); }, PROGRESS_INTERVAL_MS);
    }
  };
  timer = setTimeout(() => { void poll(); }, PROGRESS_INTERVAL_MS);

  const readStream = async () => {
    const response = await fetch("/api/assistant/stream", {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "Accept": "text/event-stream",
        "X-CSRFToken": readCsrfCookie(),
      },
      body: JSON.stringify({ profile_id: profileId, question, conversation_id: conversationId, request_id: requestId }),
      signal: controller.signal,
    });
    if (!response.ok || !response.body) {
      throw new ApiError(response.status, `Der Server antwortet mit HTTP ${response.status}.`);
    }
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    try {
      for (;;) {
        const { done: ended, value } = await reader.read();
        if (ended) break;
        buffer += decoder.decode(value, { stream: true });
        const frames = buffer.split(/\r?\n\r?\n/);
        buffer = frames.pop() ?? "";
        for (const frame of frames) {
          const line = frame.split(/\r?\n/).find((l) => l.startsWith("data:"));
          if (!line) continue;
          let event: StreamEvent;
          try { event = JSON.parse(line.slice(5).trimStart()) as StreamEvent; }
          catch { continue; }
          lastStreamEvent = Date.now();
          if (event.sequence !== undefined) {
            if (event.sequence <= sequence) continue;
            sequence = event.sequence;
          }
          deliver(event);
        }
        if (completed) break;
      }
      if (!completed) throw new Error("Die Verbindung wurde vor dem Abschluss unterbrochen. Bitte den Chatverlauf prüfen.");
    } finally {
      reader.releaseLock();
    }
  };

  try {
    // A completed snapshot can finish the UI even while the VPN still holds
    // the SSE headers/body. The persisted final message is already available.
    await Promise.race([readStream(), done]);
  } finally {
    stopped = true;
    clearTimeout(timer);
    controller.abort();
    signal?.removeEventListener("abort", abort);
  }
}

export function useDeleteConversation(profileId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (conversationId: string) =>
      apiSend<{ detail: string }>("DELETE", `/api/assistant/conversations/${conversationId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["assistant-conversations", profileId] });
      void queryClient.invalidateQueries({ queryKey: ["assistant", profileId] });
    },
  });
}
